import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Instagram, Youtube, Music as MusicIcon, ShoppingBag, Calendar, MapPin, Disc3, Mic2, Phone, ArrowRight } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { db } from '../services/firebase';
import { collection, query, orderBy, limit, onSnapshot, doc, getDoc } from 'firebase/firestore';

export default function Home() {
  const { scrollYProgress } = useScroll();
  const [recentMusic, setRecentMusic] = useState([]);
  const [upcomingShows, setUpcomingShows] = useState([]);
  const [topMerch, setTopMerch] = useState([]);
  const [socialLinks, setSocialLinks] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Parallax effect for hero
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  // Fetch real data from Firestore
  useEffect(() => {
    if (!db) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        // Fetch recent music
        const musicQuery = query(
          collection(db, 'music'),
          orderBy('releaseDate', 'desc'),
          limit(3)
        );
        const unsubscribeMusic = onSnapshot(musicQuery, (snapshot) => {
          const musicData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setRecentMusic(musicData);
        });

        // Fetch upcoming shows
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const showsQuery = query(
          collection(db, 'shows'),
          orderBy('date', 'asc'),
          limit(3)
        );
        const unsubscribeShows = onSnapshot(showsQuery, (snapshot) => {
          const showsData = snapshot.docs
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
            .filter((show) => {
              if (!show.date) return false;
              const showDate = new Date(show.date);
              return showDate >= today && show.status !== 'cancelled';
            })
            .slice(0, 3);
          setUpcomingShows(showsData);
        });

        // Fetch top merch
        const merchQuery = query(
          collection(db, 'merch'),
          orderBy('sales', 'desc'),
          limit(3)
        );
        const unsubscribeMerch = onSnapshot(merchQuery, (snapshot) => {
          const merchData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setTopMerch(merchData);
        });

        // Fetch settings for social links
        const settingsDoc = await getDoc(doc(db, 'settings', 'site'));
        if (settingsDoc.exists()) {
          setSocialLinks(settingsDoc.data().socialLinks || {});
        }

        setIsLoading(false);

        return () => {
          unsubscribeMusic();
          unsubscribeShows();
          unsubscribeMerch();
        };
      } catch (error) {
        console.error('Error fetching data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with Parallax */}
      <section className="relative h-screen flex flex-col overflow-hidden">
        {/* Parallax Background */}
        <motion.div
          className="absolute inset-0"
          style={{ y: heroY }}
        >
          <div className="absolute inset-0">
            <img
              src="/images/Scooby.png"
              alt="Scooby DaGodSon"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-red-600/70 via-orange-500/75 to-orange-400/80"></div>
          </div>
        </motion.div>

        {/* Hero Content */}
        <motion.div
          className="relative flex-1 flex items-end justify-center z-10 pb-20"
          style={{ opacity: heroOpacity }}
        >
          <div className="container mx-auto px-6">
            <div className="text-center text-white space-y-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Link to="/music">
                  <Button
                    className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-black font-semibold px-8 py-4 text-base rounded-full"
                    size="lg"
                  >
                    Stream/Download New Music
                  </Button>
                </Link>
              </motion.div>

              {/* Social Media Icons */}
              <motion.div
                className="flex items-center justify-center gap-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <a href={socialLinks.instagram || "https://www.instagram.com/scooby_dagodson/"} target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-transparent border-2 border-white rounded-lg flex items-center justify-center hover:bg-white hover:text-black transition-all">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href={socialLinks.threads || "https://www.threads.com/@scooby_dagodson?igshid=NTc4MTIwNjQ2YQ=="} target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-transparent border-2 border-white rounded-lg flex items-center justify-center hover:bg-white hover:text-black transition-all">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 192 192">
                    <path d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z"/>
                  </svg>
                </a>
                <a href={socialLinks.youtube || "https://www.youtube.com/channel/UCV_ldgJP-CMA8MjaX-iwDUg"} target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-transparent border-2 border-white rounded-lg flex items-center justify-center hover:bg-white hover:text-black transition-all">
                  <Youtube className="w-5 h-5" />
                </a>
                <a href={socialLinks.spotify || "https://open.spotify.com/artist/4W8fr5Z0qs6ubpl825w7BC?si=blLgOICQTnCQPejkGKViGg&nd=1&dlsi=a5e368d0eb714420"} target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-transparent border-2 border-white rounded-lg flex items-center justify-center hover:bg-white hover:text-black transition-all">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                </a>
              </motion.div>

              {/* Scroll Indicator */}
              <motion.div
                className="flex flex-col items-center gap-1 opacity-70"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.7 }}
                transition={{ duration: 1, delay: 0.6 }}
              >
                <span className="text-white font-semibold text-sm">Scroll to Explore</span>
                <motion.svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </motion.svg>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Booking CTA Section */}
      <motion.section
        className="py-20 px-6 bg-[#0a0a0a] text-white relative overflow-hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        {/* Subtle Background Accent */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-40 h-40 border border-orange-500 rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 border border-orange-500 rounded-full"></div>
          <div className="absolute top-1/2 left-1/4 w-20 h-20 border border-orange-500 rounded-full"></div>
        </div>

        <div className="container mx-auto relative z-10">
          <motion.div className="text-center max-w-4xl mx-auto" variants={fadeInUp}>
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-red-600/20 rounded-full flex items-center justify-center border border-orange-500/30">
                <Mic2 className="w-8 h-8 text-orange-500" />
              </div>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Book <span className="text-orange-500">Scooby DaGodSon</span> for Your Next Event
            </h2>
            <p className="text-xl md:text-2xl mb-8 text-gray-400">
              Bring the authentic Go-Go sound to your wedding, corporate event, birthday party, or private celebration
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold px-10 py-6 text-lg rounded-full flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Book Now
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-semibold px-8 py-6 text-lg rounded-full bg-transparent">
                  Learn More About Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Most Recent Music Section */}
      {!isLoading && recentMusic.length > 0 && (
        <motion.section
          id="music"
          className="py-20 px-6 bg-black text-white"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <div className="container mx-auto">
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-5xl font-bold mb-4">
                Most Recent <span className="text-orange-500">Music</span>
              </h2>
              <p className="text-gray-400 text-lg">Stream the latest releases and classic hits</p>
            </motion.div>

            <motion.div
              className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
              variants={staggerContainer}
            >
              {recentMusic.map((track) => (
                <motion.div
                  key={track.id}
                  className="group cursor-pointer"
                  variants={fadeInUp}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="bg-[#1a1a1a] rounded-lg overflow-hidden mb-4 relative">
                    {track.coverImage ? (
                      <img
                        src={track.coverImage}
                        alt={track.title}
                        className="w-full aspect-square object-cover"
                      />
                    ) : (
                      <div className="w-full aspect-square flex items-center justify-center bg-gray-800">
                        <Disc3 className="w-20 h-20 text-gray-600" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <Link to="/music" className="w-full">
                        <Button className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold">
                          <MusicIcon className="mr-2 h-4 w-4" />
                          Play Now
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-1">{track.title}</h3>
                  <p className="text-gray-400 text-sm">
                    {track.type || 'Single'} • {track.releaseDate ? new Date(track.releaseDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'New'}
                  </p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div className="text-center mt-12" variants={fadeInUp}>
              <Link to="/music">
                <Button className="bg-transparent border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-semibold px-12 py-4 text-lg rounded-full transition-all">
                  View Full Discography
                </Button>
              </Link>
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Latest Shows Section */}
      {!isLoading && upcomingShows.length > 0 && (
        <motion.section
          id="shows"
          className="py-20 px-6 bg-[#0a0a0a] text-white"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <div className="container mx-auto">
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-5xl font-bold mb-4">
                Latest <span className="text-orange-500">Shows</span>
              </h2>
              <p className="text-gray-400 text-lg">Don't miss the live performances</p>
            </motion.div>

            <motion.div
              className="max-w-4xl mx-auto space-y-6"
              variants={staggerContainer}
            >
              {upcomingShows.map((show) => {
                const showDate = show.date ? new Date(show.date) : null;
                const month = showDate ? showDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase() : 'TBA';
                const day = showDate ? showDate.getDate() : '??';
                
                return (
                  <motion.div
                    key={show.id}
                    className="bg-[#1a1a1a] rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between hover:bg-[#252525] transition-colors gap-6"
                    variants={fadeInUp}
                    whileHover={{ x: 10 }}
                  >
                    <div className="flex items-center gap-6 flex-1">
                      <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-xl p-4 text-center min-w-[80px]">
                        <div className="text-sm font-bold">{month}</div>
                        <div className="text-3xl font-bold">{day}</div>
                      </div>
                      <div className="text-center md:text-left">
                        <h3 className="text-2xl font-bold mb-1">{show.venue}</h3>
                        <div className="flex items-center justify-center md:justify-start gap-4 text-gray-400">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{show.location}</span>
                          </div>
                          {show.time && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{show.time}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {show.ticketsUrl ? (
                      <a href={show.ticketsUrl} target="_blank" rel="noopener noreferrer">
                        <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold px-8 w-full md:w-auto">
                          Get Tickets
                        </Button>
                      </a>
                    ) : (
                      <Link to="/contact">
                        <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold px-8 w-full md:w-auto">
                          Inquire
                        </Button>
                      </Link>
                    )}
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div className="text-center mt-12" variants={fadeInUp}>
              <Link to="/shows">
                <Button className="bg-transparent border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-semibold px-12 py-4 text-lg rounded-full transition-all">
                  View All Shows
                </Button>
              </Link>
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Most Bought Merch Section */}
      {!isLoading && topMerch.length > 0 && (
        <motion.section
          id="merch"
          className="py-20 px-6 bg-black text-white"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <div className="container mx-auto">
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-5xl font-bold mb-4">
                Most Bought <span className="text-orange-500">Merch</span>
              </h2>
              <p className="text-gray-400 text-lg">Fan favorites - Get yours before they're gone</p>
            </motion.div>

            <motion.div
              className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
              variants={staggerContainer}
            >
              {topMerch.map((item) => (
                <motion.div
                  key={item.id}
                  className="group cursor-pointer"
                  variants={fadeInUp}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="bg-[#1a1a1a] rounded-lg overflow-hidden mb-4 relative">
                    {item.images && item.images[0] ? (
                      <img
                        src={item.images[0]}
                        alt={item.name}
                        className="w-full aspect-square object-cover"
                      />
                    ) : (
                      <div className="aspect-square flex items-center justify-center bg-gradient-to-br from-orange-500/20 to-red-600/20">
                        <ShoppingBag className="w-20 h-20 text-gray-600" />
                      </div>
                    )}
                    {item.sales && (
                      <div className="absolute top-3 right-3 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                        {item.sales} sold
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold mb-1">{item.name}</h3>
                      <p className="text-orange-500 font-bold text-lg">
                        ${parseFloat(item.price).toFixed(2)}
                      </p>
                    </div>
                    <Link to="/merch">
                      <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold">
                        <ShoppingBag className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div className="text-center mt-12" variants={fadeInUp}>
              <Link to="/merch">
                <Button className="bg-transparent border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-semibold px-12 py-4 text-lg rounded-full transition-all">
                  Shop All Merchandise
                </Button>
              </Link>
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Second Booking CTA - Different Style */}
      <motion.section
        className="py-16 px-6 bg-black text-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <div className="container mx-auto">
          <motion.div
            className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8 border border-gray-700"
            variants={fadeInUp}
          >
            <div className="text-center md:text-left flex-1">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to <span className="text-orange-500">Book?</span>
              </h3>
              <p className="text-gray-400 text-lg">
                Get a quote for your event today. We perform at weddings, corporate events, clubs, and private parties across the DMV area and beyond.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold px-8 py-4 text-lg rounded-full whitespace-nowrap">
                  Get a Quote
                </Button>
              </Link>
              <a href="tel:+12025551234">
                <Button variant="outline" className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-semibold px-8 py-4 text-lg rounded-full flex items-center gap-2 bg-transparent">
                  <Phone className="w-5 h-5" />
                  Call Us
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Instagram Feed Section */}
      <motion.section
        id="instagram"
        className="py-20 px-6 bg-[#0a0a0a] text-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <div className="container mx-auto">
          <motion.div className="text-center mb-16" variants={fadeInUp}>
            <div className="flex items-center justify-center gap-3 mb-4">
              <Instagram className="w-8 h-8 text-orange-500" />
              <h2 className="text-5xl font-bold">
                <span className="text-orange-500">@ScoobyDaGodSon</span>
              </h2>
            </div>
            <p className="text-gray-400 text-lg">Follow for behind-the-scenes and latest updates</p>
          </motion.div>

          <motion.div
            className="flex justify-center"
            variants={fadeInUp}
          >
            <a
              href={socialLinks.instagram || "https://www.instagram.com/scooby_dagodson/"}
              target="_blank"
              rel="noopener noreferrer"
              className="group"
            >
              <div className="w-64 h-64 md:w-80 md:h-80 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-3xl p-1 hover:scale-105 transition-transform duration-300">
                <div className="w-full h-full bg-[#1a1a1a] rounded-3xl flex flex-col items-center justify-center gap-4">
                  <Instagram className="w-20 h-20 text-white group-hover:text-orange-500 transition-colors" />
                  <p className="text-white text-xl font-bold">@scooby_dagodson</p>
                  <p className="text-gray-400 text-sm">Tap to follow</p>
                </div>
              </div>
            </a>
          </motion.div>

          <motion.div className="text-center mt-12" variants={fadeInUp}>
            <a
              href={socialLinks.instagram || "https://www.instagram.com/scooby_dagodson/"}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold px-12 py-4 text-lg rounded-full">
                <Instagram className="mr-2 h-5 w-5" />
                Follow on Instagram
              </Button>
            </a>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
}
